
import { useContext, createContext, ReactNode, useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/components/AuthProvider";
import { Achievement, AchievementProgress, AchievementType, AchievementFilter } from "@/types/achievementTypes";
import { toast } from "sonner";

interface AchievementContextType {
  achievements: Achievement[];
  loadingAchievements: boolean;
  unlockAchievement: (achievementId: string) => Promise<void>;
  checkAchievementStatus: (achievementId: string) => boolean;
  incrementAchievementProgress: (achievementId: string, increment?: number) => Promise<void>;
  getAchievementProgress: (achievementId: string) => number;
  refreshAchievements: () => void;
  activeFilter: AchievementType | "all";
  setActiveFilter: (filter: AchievementType | "all") => void;
  achievementCounts: Record<AchievementType | "all", number>;
  refetch: () => Promise<any>;
}

const AchievementContext = createContext<AchievementContextType | undefined>(undefined);

export function AchievementProvider({ children }: { children: ReactNode }) {
  // Try to use Auth, but with a fallback for when it's not available
  let user = null;
  try {
    const authContext = useAuth();
    user = authContext?.user;
  } catch (error) {
    console.warn("Auth context not available in AchievementProvider");
  }

  const [unlockedAchievements, setUnlockedAchievements] = useState<Record<string, boolean>>({});
  const [achievementProgress, setAchievementProgress] = useState<Record<string, number>>({});
  const [activeFilter, setActiveFilter] = useState<AchievementType | "all">("all");
  const [achievementCounts, setAchievementCounts] = useState<Record<AchievementType | "all", number>>({} as Record<AchievementType | "all", number>);

  // Fetch achievements from Supabase
  const { data: achievements, isLoading: loadingAchievements, refetch } = useQuery({
    queryKey: ["achievements"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("achievements")
        .select("*")
        .order("id");

      if (error) {
        console.error("Error fetching achievements:", error);
        return [];
      }

      // Explicitly cast the data to the Achievement type
      return data as unknown as Achievement[];
    },
  });

  // Fetch user's unlocked achievements
  const { data: userAchievements, refetch: refetchUserAchievements } = useQuery({
    queryKey: ["user-achievements", user?.id],
    queryFn: async () => {
      if (!user) return [];

      const { data, error } = await supabase
        .from("user_achievements")
        .select("*")
        .eq("user_id", user.id);

      if (error) {
        console.error("Error fetching user achievements:", error);
        return [];
      }

      return data;
    },
    enabled: !!user,
  });

  // Use localStorage for achievement progress
  const fetchAchievementProgress = async () => {
    if (!user) return [];
    
    try {
      // Get from localStorage as our fallback approach
      const storedProgressStr = localStorage.getItem(`achievement-progress-${user.id}`);
      if (storedProgressStr) {
        return JSON.parse(storedProgressStr) as AchievementProgress[];
      }
      return [] as AchievementProgress[];
    } catch (e) {
      console.error("Error fetching achievement progress from localStorage:", e);
      return [] as AchievementProgress[];
    }
  };

  // Updated query for achievement progress (using localStorage)
  const { data: userProgress, refetch: refetchUserProgress } = useQuery({
    queryKey: ["achievement-progress", user?.id],
    queryFn: fetchAchievementProgress,
    enabled: !!user,
  });

  // Calculate achievement counts by type
  useEffect(() => {
    if (achievements) {
      const counts: Record<AchievementType | "all", number> = { all: achievements.length } as Record<AchievementType | "all", number>;
      
      achievements.forEach(achievement => {
        if (!counts[achievement.type]) {
          counts[achievement.type] = 0;
        }
        counts[achievement.type]++;
      });
      
      setAchievementCounts(counts);
    }
  }, [achievements]);

  // Update state when user achievements data changes
  useEffect(() => {
    if (userAchievements) {
      const unlocked: Record<string, boolean> = {};
      userAchievements.forEach((ua) => {
        unlocked[ua.achievement_id] = true;
      });
      setUnlockedAchievements(unlocked);
    }
  }, [userAchievements]);

  // Update state when user progress data changes
  useEffect(() => {
    if (userProgress) {
      const progress: Record<string, number> = {};
      userProgress.forEach((up: AchievementProgress) => {
        progress[up.achievement_id] = up.progress;
      });
      setAchievementProgress(progress);
    }
  }, [userProgress]);

  // Check if an achievement is unlocked
  const checkAchievementStatus = (achievementId: string): boolean => {
    return !!unlockedAchievements[achievementId];
  };

  // Get progress for an achievement
  const getAchievementProgress = (achievementId: string): number => {
    return achievementProgress[achievementId] || 0;
  };

  // Helper function to save progress to local storage
  const saveProgressToLocalStorage = (achievementId: string, newProgress: number) => {
    if (!user) return;
    
    try {
      const storedProgressStr = localStorage.getItem(`achievement-progress-${user.id}`);
      const storedProgress: AchievementProgress[] = storedProgressStr ? JSON.parse(storedProgressStr) : [];
      
      const existingIndex = storedProgress.findIndex(p => 
        p.achievement_id === achievementId && p.user_id === user.id
      );
      
      if (existingIndex >= 0) {
        storedProgress[existingIndex].progress = newProgress;
        storedProgress[existingIndex].updated_at = new Date().toISOString();
      } else {
        storedProgress.push({
          user_id: user.id,
          achievement_id: achievementId,
          progress: newProgress,
          updated_at: new Date().toISOString()
        });
      }
      
      localStorage.setItem(`achievement-progress-${user.id}`, JSON.stringify(storedProgress));
    } catch (e) {
      console.error("Error saving progress to localStorage:", e);
    }
  };

  // Unlock an achievement (server-validated)
  const unlockAchievement = async (achievementId: string): Promise<void> => {
    if (!user || checkAchievementStatus(achievementId)) return;

    try {
      // Server re-validates the user actually qualifies before inserting.
      const { data, error } = await supabase.rpc("unlock_achievements", {
        _achievement_ids: [achievementId],
      });

      if (error) throw error;

      const granted = (data ?? []).some(
        (row: { achievement_id: string }) => row.achievement_id === achievementId
      );
      if (!granted) return;

      // Update local state
      setUnlockedAchievements((prev) => ({
        ...prev,
        [achievementId]: true,
      }));

      // Get achievement details for the toast
      const achievement = achievements?.find((a) => a.id === achievementId);
      if (achievement) {
        toast.success(`🏆 Achievement Unlocked: ${achievement.title}`, {
          description: achievement.description,
          duration: 5000,
        });
      }

      // Refetch data
      await refetchUserAchievements();
    } catch (error) {
      console.error("Error unlocking achievement:", error);
    }
  };

  // Increment achievement progress
  const incrementAchievementProgress = async (
    achievementId: string,
    increment = 1
  ): Promise<void> => {
    if (!user || checkAchievementStatus(achievementId)) return;

    try {
      const currentProgress = getAchievementProgress(achievementId);
      const newProgress = currentProgress + increment;

      // Find the target achievement
      const achievement = achievements?.find((a) => a.id === achievementId);
      if (!achievement) return;

      // Save to localStorage (since we don't have the table yet)
      saveProgressToLocalStorage(achievementId, newProgress);

      // Update local state
      setAchievementProgress((prev) => ({
        ...prev,
        [achievementId]: newProgress,
      }));

      // Check if this progress should unlock the achievement
      if (
        achievement.target_value &&
        newProgress >= achievement.target_value &&
        !checkAchievementStatus(achievementId)
      ) {
        await unlockAchievement(achievementId);
      }

      // Refetch data
      await refetchUserProgress();
    } catch (error) {
      console.error("Error updating achievement progress:", error);
    }
  };

  // Helper to refresh all achievement data
  const refreshAchievements = () => {
    refetch();
    refetchUserAchievements();
    refetchUserProgress();
  };

  // Prepare achievements with unlocked status
  const achievementsWithStatus: Achievement[] = (achievements || []).map(
    (achievement) => ({
      ...achievement,
      isUnlocked: checkAchievementStatus(achievement.id),
      progress: getAchievementProgress(achievement.id),
    })
  );

  return (
    <AchievementContext.Provider
      value={{
        achievements: achievementsWithStatus,
        loadingAchievements,
        unlockAchievement,
        checkAchievementStatus,
        incrementAchievementProgress,
        getAchievementProgress,
        refreshAchievements,
        activeFilter,
        setActiveFilter,
        achievementCounts,
        refetch,
      }}
    >
      {children}
    </AchievementContext.Provider>
  );
}

export function useAchievements() {
  const context = useContext(AchievementContext);
  if (context === undefined) {
    throw new Error("useAchievements must be used within an AchievementProvider");
  }
  return context;
}
